import React from 'react';
import { X, FilePlus, ImagePlus, ArrowRight, Sparkles } from 'lucide-react';
import { Language } from '../types';

interface SnapshotDecisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: string | null;
  hasSelectedNode: boolean;
  selectedNodeTitle?: string;
  isSelectedNodeRoot?: boolean;
  onAddToSelected: () => void;
  onCreateNew: () => void;
  onAskAI: () => void;
  language: Language;
}

export const SnapshotDecisionModal: React.FC<SnapshotDecisionModalProps> = ({
  isOpen,
  onClose,
  image,
  hasSelectedNode,
  selectedNodeTitle,
  onAddToSelected,
  onCreateNew,
  onAskAI,
  language
}) => {
  if (!isOpen || !image) return null;

  const isVi = language === 'vietnamese';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-slate-950">
           <h3 className="font-bold text-lg text-gray-900 dark:text-white">
             {isVi ? 'Đã chụp ảnh' : 'Snapshot Captured'}
           </h3>
           <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-slate-800 rounded-full transition-colors text-gray-500">
             <X size={20} />
           </button>
        </div>

        <div className="p-6">
          <div className="mb-6 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 bg-gray-100 dark:bg-slate-800 flex items-center justify-center">
            <img src={image} alt="Snapshot Preview" className="max-h-48 object-contain" />
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 text-center">
            {isVi ? 'Bạn muốn lưu hình ảnh này vào đâu?' : 'Where would you like to place this image?'}
          </p>

          <div className="space-y-3">
            <button
              onClick={onAddToSelected}
              disabled={!hasSelectedNode}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 group ${
                hasSelectedNode
                  ? 'border-blue-100 dark:border-blue-900/30 bg-blue-50 dark:bg-blue-900/10 hover:border-blue-500 cursor-pointer'
                  : 'border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 opacity-50 cursor-not-allowed'
              }`}
            >
              <div className={`p-3 rounded-full ${hasSelectedNode ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                <ImagePlus size={20} />
              </div>
              <div className="flex-1">
                <div className="font-bold text-gray-900 dark:text-white text-sm">
                  {isVi ? 'Thêm vào ghi chú đang chọn' : 'Add to Selected Note'}
                </div>
                {hasSelectedNode && (
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 truncate max-w-[180px]">
                    {isVi ? 'Đang chọn' : 'Selected'}: "{selectedNodeTitle || 'Untitled'}"
                  </div>
                )}
              </div>
              {hasSelectedNode && <ArrowRight size={18} className="text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />}
            </button>

            <button
              onClick={onCreateNew}
              className="w-full p-4 rounded-xl border-2 border-green-100 dark:border-green-900/30 bg-green-50 dark:bg-green-900/10 hover:border-green-500 cursor-pointer text-left transition-all flex items-center gap-4 group"
            >
              <div className="p-3 rounded-full bg-green-500 text-white">
                <FilePlus size={20} />
              </div>
              <div className="flex-1">
                <div className="font-bold text-gray-900 dark:text-white text-sm">
                  {isVi ? 'Tạo ghi chú mới' : 'Create New Note'}
                </div>
                <div className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                  {isVi ? 'Tạo ghi chú con trong nhánh hiện tại' : 'Create as child of current branch'}
                </div>
              </div>
              <ArrowRight size={18} className="text-green-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>

            <button
              onClick={onAskAI}
              className="w-full p-4 rounded-xl border-2 border-violet-100 dark:border-violet-900/30 bg-violet-50 dark:bg-violet-900/10 hover:border-violet-500 cursor-pointer text-left transition-all flex items-center gap-4 group"
            >
              <div className="p-3 rounded-full bg-violet-500 text-white">
                <Sparkles size={20} />
              </div>
              <div className="flex-1">
                <div className="font-bold text-gray-900 dark:text-white text-sm">
                  {isVi ? 'Hỏi AI về đoạn này' : 'Ask AI about this'}
                </div>
                <div className="text-xs text-violet-600 dark:text-violet-400 mt-0.5">
                  {isVi ? 'Xem giải thích AI, rồi tự viết ghi chú của bạn' : 'Get an explanation, then write your own note'}
                </div>
              </div>
              <ArrowRight size={18} className="text-violet-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
